import { useState } from 'react'
import { Medal, Plus, Trash2 } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { getal } from '../lib/opmaak'
import { Lade } from '../onderdelen/Lade'
import { AfbeeldingKiezen } from './AfbeeldingKiezen'
import { Laden, useLaad } from './Beheer'

/*
 * Rangen (tiers) op het aantal wasbeurten, en de voordelen (perks) per rang.
 * De rang van een klant wordt berekend: een drempel aanpassen geldt meteen
 * voor iedereen.
 */

interface TierRij { id: string; naam: string; drempel: number; kleur: string | null; afbeelding: string | null; klanten: number }
interface PerkRij { id: string; naam: string; tiers: string[] }
interface Data { tiers: TierRij[]; perks: PerkRij[] }

export function Rangen() {
  const { data, fout, laad, zet } = useLaad<Data>('tiers')
  const [bewerk, zetBewerk] = useState<Partial<TierRij> | null>(null)
  const [nieuwPerk, zetNieuwPerk] = useState('')
  const [melding, zetMelding] = useState<string | null>(null)
  if (!data) return <><div className="pagina-kop"><h2>Rangen</h2></div><Laden fout={fout} opnieuw={laad} /></>

  const zetPerk = async (perk: PerkRij, tier: string, aan: boolean) => {
    const tiers = aan ? [...perk.tiers, tier] : perk.tiers.filter((x) => x !== tier)
    try { zet(await vraag('perk-opslaan', { id: perk.id, naam: perk.naam, tiers })) } catch (e) { zetMelding(foutTekst(e)) }
  }

  return (
    <>
      <div className="pagina-kop">
        <h2>Rangen</h2>
        <button className="knop geel" onClick={() => zetBewerk({ kleur: '#f8c010' })}><Plus size={19} />Nieuwe rang</button>
      </div>
      <p className="stil" style={{ marginBottom: 14 }}>Een klant groeit door op het aantal wasbeurten (oude transacties plus elke scan in TW1 Club). Hij ziet zijn rang en de voortgang op zijn telefoon en na elke scan op de tablet.</p>
      {melding && <div className="melding fout" style={{ marginBottom: 12 }}>{melding}</div>}

      <div className="rang-raster">
        {data.tiers.map((t) => (
          <button key={t.id} className="rang-tegel" style={{ ['--rang' as string]: t.kleur ?? 'var(--brand)' }} onClick={() => zetBewerk(t)}>
            {t.afbeelding ? <img src={t.afbeelding} alt="" /> : <span className="rang-badge leeg"><Medal size={26} /></span>}
            <b>{t.naam}</b>
            <span className="stil klein">vanaf {getal(t.drempel)} wasbeurten</span>
            <span className="rang-aantal cijfer">{getal(t.klanten)} <small>klanten</small></span>
          </button>
        ))}
      </div>

      <section className="kaart vol" style={{ marginTop: 18 }}>
        <div className="sectie-kop" style={{ marginTop: 0 }}><h2>Voordelen</h2></div>
        <p className="stil klein" style={{ marginBottom: 10 }}>Vink aan bij welke rangen een voordeel hoort; de klant ziet ze bij zijn rang.</p>
        <div className="voordelen-kaartjes">
          {data.perks.map((p) => (
            <div key={p.id} className="voordeel-kaart">
              <div className="rij" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                <b>{p.naam}</b>
                <button className="knop stil-knop rond" aria-label="Weghalen" onClick={async () => {
                  if (confirm(`Voordeel "${p.naam}" weghalen?`)) zet(await vraag('perk-verwijderen', { id: p.id }))
                }}><Trash2 size={17} /></button>
              </div>
              <div className="chips" style={{ marginBottom: 0 }}>
                {data.tiers.map((t) => (
                  <button key={t.id} className="chip" aria-pressed={p.tiers.includes(t.id)} onClick={() => zetPerk(p, t.id, !p.tiers.includes(t.id))}>
                    {p.tiers.includes(t.id) ? '✓ ' : ''}{t.naam.replace(/^Rang \d+ - /, '')}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="tabel-schuif voordelen-tabel">
          <table className="tabel">
            <thead><tr><th>Voordeel</th>{data.tiers.map((t) => <th key={t.id} style={{ textAlign: 'center' }}>{t.naam.replace(/^Rang \d+ - /, '')}</th>)}<th /></tr></thead>
            <tbody>
              {data.perks.map((p) => (
                <tr key={p.id}>
                  <td><b>{p.naam}</b></td>
                  {data.tiers.map((t) => (
                    <td key={t.id} style={{ textAlign: 'center' }}>
                      <input type="checkbox" style={{ width: 22, height: 22, accentColor: 'var(--brand)' }} checked={p.tiers.includes(t.id)}
                        onChange={(e) => zetPerk(p, t.id, e.target.checked)} aria-label={`${p.naam} bij ${t.naam}`} />
                    </td>
                  ))}
                  <td><button className="knop stil-knop rond" aria-label="Weghalen" onClick={async () => {
                    if (confirm(`Voordeel "${p.naam}" weghalen?`)) zet(await vraag('perk-verwijderen', { id: p.id }))
                  }}><Trash2 size={17} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <form className="filter-balk" style={{ marginTop: 12 }} onSubmit={async (e) => {
          e.preventDefault()
          if (!nieuwPerk.trim()) return
          try { zet(await vraag('perk-opslaan', { naam: nieuwPerk, tiers: data.tiers.map((t) => t.id) })); zetNieuwPerk('') } catch (x) { zetMelding(foutTekst(x)) }
        }}>
          <input className="invoer" value={nieuwPerk} onChange={(e) => zetNieuwPerk(e.target.value)} placeholder="Nieuw voordeel" />
          <button className="knop"><Plus size={18} />Toevoegen</button>
        </form>
      </section>

      <Lade titel={bewerk?.id ? bewerk.naam ?? '' : 'Nieuwe rang'} open={!!bewerk} sluit={() => zetBewerk(null)}
        kinderen={bewerk && <RangFormulier t={bewerk} klaar={(r) => { zet(r); zetBewerk(null) }}
          bijgewerkt={(r: Data) => { zet(r); zetBewerk(r.tiers.find((x) => x.id === bewerk.id) ?? bewerk) }} />} />
    </>
  )
}

function RangFormulier({ t, klaar, bijgewerkt }: { t: Partial<TierRij>; klaar: (r: Data) => void; bijgewerkt: (r: Data) => void }) {
  const [naam, zetNaam] = useState(t.naam ?? '')
  const [drempel, zetDrempel] = useState(String(t.drempel ?? ''))
  const [kleur, zetKleur] = useState(t.kleur ?? '#f8c010')
  const [fout, zetFout] = useState<string | null>(null)
  return (
    <form className="formulier" onSubmit={async (e) => {
      e.preventDefault(); zetFout(null)
      try { klaar(await vraag('tier-opslaan', { id: t.id, naam, drempel: Number(drempel), kleur })) } catch (x) { zetFout(foutTekst(x)) }
    }}>
      <label className="veld"><span>Naam</span><input value={naam} onChange={(e) => zetNaam(e.target.value)} placeholder="Rang 3 - Barre Gaander" required /></label>
      <div className="twee-kol">
        <label className="veld"><span>Vanaf hoeveel wasbeurten</span><input value={drempel} inputMode="numeric" onChange={(e) => zetDrempel(e.target.value.replace(/\D/g, ''))} required /></label>
        <label className="veld"><span>Kleur</span>
          <span className="rij" style={{ flexWrap: 'nowrap' }}>
            <input type="color" value={kleur} onChange={(e) => zetKleur(e.target.value)} style={{ padding: 4, height: 48, width: 72, flex: 'none' }} />
          </span></label>
      </div>
      <div className="veld"><span>Badge</span>
        {t.id ? <AfbeeldingKiezen actie="tier-afbeelding" id={t.id} url={t.afbeelding ?? null} bijgewerkt={bijgewerkt} /> : <small>Sla de rang eerst op; daarna kun je een badge toevoegen.</small>}
      </div>
      {fout && <div className="melding fout">{fout}</div>}
      <div className="knoppenrij"><button className="knop geel breed">Opslaan</button></div>
      {t.id && <button type="button" className="knop stil-knop klein-knop" style={{ justifySelf: 'center', color: 'var(--text-danger)' }} onClick={async () => {
        if (confirm(`Rang "${t.naam}" weghalen? Klanten vallen dan in de rang eronder.`)) klaar(await vraag('tier-verwijderen', { id: t.id }))
      }}><Trash2 size={16} />Rang weghalen</button>}
    </form>
  )
}
